import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
} from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiCalendarComponent } from '@components/ui-calendar/ui-calendar.component';
import { UiSkippedDaysCalendarItemComponent } from '@components/ui-forms/skipped-days/calendar-item/calendar-item.component';
import { UiSettingForm } from '@components/ui-forms/ui-setting-form';
import {
    FeatureFlag,
    IExcludedCalendar,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { FileSizePipe } from '@pipes/file-size.pipe';
import {
    FileUploadService,
    ICSVParseErrorCode,
    ICSVParseErrorType,
    ModalService,
} from '@services';
import { CalendarService } from '@services/calendar.service';
import { SUPPORTED_DATE_FORMATS } from '@services/generics/constants';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';

import * as moment from 'moment';
import {
    BehaviorSubject,
    iif,
    of,
    throwError,
} from 'rxjs';
import {
    filter,
    finalize,
    map,
    switchMap,
    tap,
} from 'rxjs/operators';

const MAX_FILE_SIZE = 128 * 1024;

@Component({
    selector: 'ui-skipped-days-settings',
    templateUrl: './ui-skipped-days-settings.component.html',
    styleUrls: [
        '../ui-setting-form.scss',
        './ui-skipped-days-settings.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiSkippedDaysSettingsComponent
    extends UiSettingForm
    implements OnInit, OnChanges, AfterViewInit {

    public get dateFormatTooltip() {
        return this._translateService.instant('skipped_days_csv_date_format') + '\r\n' +
            SUPPORTED_DATE_FORMATS.slice(0, 3).join('\r\n');
    }

    @Input()
    public selected = 0;

    @Input()
    public calendars!: IExcludedCalendar[];

    @Output()
    public calendarsChange = new EventEmitter<IExcludedCalendar[]>();

    @Output()
    public selectionChange = new EventEmitter<number>();

    @Output()
    public creatingEntry = new EventEmitter<boolean>();

    public isMultipleCalendarsEnabled = this._appState.settings.isFeatureEnabled(FeatureFlag.MultipleNonWorkingCalendars);

    public today = moment();

    public loading$ = new BehaviorSubject(false);
    public loadingFile$ = new BehaviorSubject(false);

    public selectedCalendarItem?: UiSkippedDaysCalendarItemComponent;
    public selectedCalendar?: IExcludedCalendar;

    @ViewChildren('calendarItems')
    public calendarItems!: QueryList<UiSkippedDaysCalendarItemComponent>;

    @ViewChild('calendarComponent', { static: false })
    public calendarComponent!: UiCalendarComponent;

    public get calendarPermissions() {
        return this.selectedCalendar && this.selectedCalendar.newEntry ? 'Settings.Create' : 'Settings.Edit';
    }

    constructor(
        cd: ChangeDetectorRef,
        appState: AppStateFacade,
        private _fb: FormBuilder,
        private _modalService: ModalService,
        private _translateService: TranslateService,
        private _fileUploadService: FileUploadService,
        private _fileSizePipe: FileSizePipe,
        private _calendarService: CalendarService,
        private _snack: UiSnackBarService,
    ) {
        super(
            cd,
            appState,
        );
    }

    ngOnInit() {
        const calendarsInitialized = this.calendars && this.calendars.length > this.selected;
        const dates = calendarsInitialized ? this.calendars[this.selected].excludedDates : [];
        this.formGroup = this._fb.group({
            'date': [dates],
        });

        if (calendarsInitialized) {
            this.selectedCalendar = this.calendars[this.selected];
            this._loadCalendarDetail(this.selected);
        }
    }

    ngAfterViewInit() {
        this.selectedCalendarItem = this.calendarItems && this.calendarItems.find((_, idx) => idx === this.selected);
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        if (changes.dates && changes.dates.currentValue) {
            (<FormGroup>this.formGroup).patchValue({ date: changes.dates.currentValue });
        }
    }

    public createCalendarEntry() {
        // Add a new empty entry, to create dummy calendar
        this.calendars.unshift({
            calendarName: '',
            excludedDates: [] as Date[],
            newEntry: true,
        } as IExcludedCalendar);

        // Switch active calendar to newly created entry and focus input
        this._cd.detectChanges();
        this._changeSelected(0);
        this.creatingEntry.emit(true);
    }

    public onSelect(index: number) {
        if (this.selected !== index) {
            if ((<FormGroup>this.formGroup).dirty) {
                this._modalService.onConfirm(null,
                    () => {
                        (<FormGroup>this.formGroup).reset();
                        this._changeSelected(index);
                    },
                    () => { },
                    this._translateService.instant('skipped_days_calendar_unsaved_changes_warning'));
            } else {
                this._changeSelected(index);
            }
        }
    }

    public onDelete() {
        this._modalService.onConfirm(null,
            () => {
                if (this.selectedCalendar && this.selectedCalendar.newEntry) {
                    this._setCalendar([]);
                    this.selectedCalendarItem!.formControl.reset();
                    this.calendars.shift();
                    this.creatingEntry.emit(false);
                    if (this.calendars.length) {
                        this._changeSelected(0);
                    }
                    return;
                }
                const previouslySelected = this.selected;
                this._calendarService.delete(this.selectedCalendar!.calendarId)
                    .pipe(
                        tap(() => {
                            if (this.calendars && this.calendars.length > 0) {
                                this.selected = --this.selected >= 0 ? this.selected : 0;
                            }
                        }),
                        finalize(() => {
                            this.formGroup.markAsPristine();
                            this._cd.detectChanges();
                        }),
                    )
                    .subscribe(() => {
                        this.calendars.splice(previouslySelected, 1);
                        this._changeSelected(this.selected);
                    });
            },
            () => { },
            this._translateService.instant('skipped_days_calendar_delete_warning'));
    }

    public onSave(calendar: IExcludedCalendar) {
        if (!this.selectedCalendarItem!.formControl.dirty && !this.formGroup.get('date')!.dirty) {
            return;
        }
        this.loading$.next(true);

        if (this.formGroup.get('date')!.dirty) {
            calendar.excludedDates = this.formGroup.get('date')!
                .value
                .map((d: moment.MomentInput) => moment(d).toDate());
        }

        calendar.calendarName = this.selectedCalendarItem && this.selectedCalendarItem.formControl.value.trim();
        const createOrUpdateCalendar$ = calendar.newEntry ?
            this._calendarService.add(calendar) :
            this._calendarService.updateAndMap(calendar.calendarId, calendar);

        iif(() => this.selectedCalendarItem!.formControl.dirty,
            this._calendarService.isCalendarNameValid(calendar.calendarName),
            of(true),
        ).pipe(
            switchMap(() => createOrUpdateCalendar$),
            finalize(() => {
                this.loading$.next(false);
                this._cd.detectChanges();
            }),
            filter((savedCalendar) => !!savedCalendar),
        ).subscribe((savedCalendar) => {
            Object.assign(calendar, savedCalendar);
            this.formGroup.get('date')!.reset(calendar.excludedDates);
            this.selectedCalendarItem!.formControl.reset(calendar.calendarName);
            delete calendar.newEntry;
            this.calendarComponent.goToDate(moment());
            this.creatingEntry.emit(false);
        }, this._showInvalidNameError);
    }

    public onFileChange(files: File[]) {
        if (!this._fileUploadService.isFileItemValid(files[0], ['.csv'], MAX_FILE_SIZE)) {
            this._snack.error(this._translateService.instant(`file_size_too_large`, {
                maxSize: this._fileSizePipe.transform(MAX_FILE_SIZE, 0),
            }));
        }
        const parseFile$ = this._calendarService.parseFile(files[0])
            .pipe(
                tap((csv) => {
                    this.loadingFile$.next(true);
                    csv.errors = csv.errors.filter(err => err.code !== ICSVParseErrorCode.UndetectableDelimiter);
                    if (csv.errors && csv.errors.length > 0) {
                        const errorReason = ((errorType) => {
                            switch (errorType) {
                                case ICSVParseErrorType.FieldMismatch:
                                    return this._translateService.instant('csv_parser_error_field_mismatch');
                                case ICSVParseErrorType.FieldError:
                                    return this._translateService.instant('csv_parser_error_incorrect_date_format');
                            }
                        })(csv.errors[0].type);

                        this._snack.error(this._translateService.instant('skipped_days_calendar_csv_errors', {
                            reason: errorReason,
                        }));
                        throwError(csv.errors);
                    }
                }),
                map((csv) => {
                    return csv.data
                        .filter((date: any) => date.excludedDate)
                        .map((date: any) => moment(date.excludedDate).utc(true));
                }),
                tap(data => {
                    this._setCalendar(data);
                    this.formGroup.get('date')!.markAsDirty();
                }),
                finalize(() => this.loadingFile$.next(false)),
            );
        if (this.calendars[this.selected].excludedDates.length) {
            this._modalService.onConfirm(
                null,
                () => parseFile$.subscribe(),
                () => { },
                this._translateService.instant('skipped_days_calendar_csv_confirm_override'),
            );
        } else {
            parseFile$.subscribe();
        }
    }

    @HostListener('document:keydown.escape')
    public onEscape() {
        if (this.calendars.some(cal => !!cal.newEntry) || this.formGroup.get('date')!.dirty) {
            this._modalService.onConfirm(
                null,
                () => this._reset(),
                () => { },
                this._translateService.instant('skipped_days_clear_unadded_warning'),
            );
        }
    }

    public trackById(calendar: IExcludedCalendar) {
        return calendar.calendarId;
    }

    private _changeSelected(index: number) {
        this.selected = index;
        this.selectedCalendarItem = this.calendarItems.find((_, idx) => idx === index);
        this.selectedCalendar = this.calendars[this.selected];
        this.calendarComponent.goToDate(moment());

        if (this.selectedCalendar && this.selectedCalendar.calendarId) {
            this.selectionChange.emit(index);
            this._loadCalendarDetail(index);
        } else {
            this._setCalendar([]);
        }
    }

    private _loadCalendarDetail(index: number) {
        if (!this.calendars || !this.calendars[index]) {
            return;
        }

        this._setCalendar([]);

        const currentlySelectedCalendar = this.calendars[index];
        if (currentlySelectedCalendar.loading == null) {
            currentlySelectedCalendar.loading = true;
            this.loading$.next(true);
            this._calendarService.getInstance(currentlySelectedCalendar.calendarId)
                .pipe(
                    finalize(() => this.loading$.next(false)),
                )
                .subscribe(
                    (calendar) => {
                        // This prevents calendar details from being loaded if the selection is changed before the data comes in
                        currentlySelectedCalendar.calendarId = calendar.calendarId;
                        currentlySelectedCalendar.timeZoneId = calendar.timeZoneId;
                        currentlySelectedCalendar.excludedDates = calendar.excludedDates;
                        this._setCalendar(currentlySelectedCalendar.excludedDates);
                        currentlySelectedCalendar.loading = false;
                    },
                    () => delete currentlySelectedCalendar.loading,
                );
        } else {
            this._setCalendar(currentlySelectedCalendar.excludedDates);
        }
    }

    private _setCalendar(dates: Date[]) {
        this.formGroup.get('date')!.patchValue(dates);
    }

    private _reset() {
        this.calendars = this.calendars.filter(cal => !cal.newEntry);
        this.creatingEntry.emit(false);
        if (this.calendars && this.calendars.length) {
            this.formGroup.get('date')!.reset(this.selectedCalendar!.excludedDates);
            this._changeSelected(0);
        }
        this._cd.detectChanges();
    }

    private _showInvalidNameError = () => {
        this._snack.error(this._translateService.instant('skipped_days_duplicate_error'));
        this.selectedCalendarItem!.formGroup.get('name')!.setErrors({
            notUnique: true,
        });
    }
}
